import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { api } from "../lib/api";
import AiAnalysisPanel from "../components/AiAnalysisPanel";
import { useAuth } from "../lib/auth";

export default function AiAnalysis() {
  const { user } = useAuth();
  const isOrgMember = user?.role === "org_member";
  const [searchParams, setSearchParams] = useSearchParams();
  const urlProject = searchParams.get("project") || "";
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(urlProject);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Consumed-once question from the floating Ask-AI bar + per-result Ask AI.
  const [autoQuestion, setAutoQuestion] = useState(searchParams.get("q") || "");
  const autoNew = searchParams.get("new") === "1";
  const [autoResultId, setAutoResultId] = useState(searchParams.get("resultId") || searchParams.get("result_id") || "");

  const clearAutoParams = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("q");
    next.delete("new");
    next.delete("resultId");
    next.delete("result_id");
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const r = await api.get("/projects");
        const list = r.data?.projects || [];
        setProjects(list);
        if (list.length > 0) {
          const preferred = list.find((p) => p.id === urlProject);
          // AI defaults to "all projects" (unified) unless one is specified in the URL
          setProjectId(preferred ? preferred.id : "");
        }
      } catch {
        setError("Failed to load projects");
      } finally {
        setLoading(false);
      }
    };
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (projectId) next.set("project", projectId);
    else next.delete("project");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-4">
      <div className="mb-5">
        <div>
          <h1 className="text-2xl font-bold text-[#0F1F3D] flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-[#0F1F3D]" /> Analysis
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1D4ED8] text-[10px] font-bold uppercase tracking-wide">
              Beta
            </span>
          </h1>
          <p className="text-sm text-[#64748B] mt-1">
            Investigate what&apos;s changing across your project with AI.
          </p>
          {projects.length > 0 && (
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              data-testid="ai-analysis-project-select"
              className="mt-3 px-4 rounded-lg border border-[#CBD5E1] bg-white text-base font-semibold text-[#0F1F3D] min-h-[40px] min-w-[220px] focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]/20 shadow-sm"
            >
              <option value="">All my projects</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-[#64748B]" data-testid="ai-analysis-loading">
          Loading…
        </div>
      ) : error ? (
        <div
          className="p-6 rounded-xl bg-[#FEF2F2] text-[#B91C1C]"
          data-testid="ai-analysis-error"
        >
          {error}
        </div>
      ) : projects.length === 0 ? (
        <div
          className="rounded-2xl border border-dashed border-[#CBD5E1] bg-white p-10 text-center"
          data-testid="ai-analysis-empty"
        >
          <p className="text-[#64748B]">No projects yet.</p>
          {!isOrgMember && (
            <Link
              to="/app/projects/new"
              data-testid="ai-analysis-create-project"
              className="text-[#0F1F3D] font-semibold hover:underline focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]/20 rounded-sm"
            >
              Click Here to create your first project
            </Link>
          )}
        </div>
      ) : (
        <AiAnalysisPanel
          projectId={projectId}
          autoQuestion={autoQuestion}
          autoNew={autoNew}
          autoResultId={autoResultId}
          onConsumed={() => {
            setAutoQuestion("");
            setAutoResultId("");
            clearAutoParams();
          }}
        />
      )}
    </div>
  );
}